import React from 'react';
import { Table, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createUseStyles } from 'react-jss';
import { CaretDownOutlined, CaretUpOutlined, FileSearchOutlined } from '@ant-design/icons';

import type { SimulationDetail, SimulationReportingTableRecord } from '../../store/simulation/types';
import { useStoreState } from '../../store';
import { palette } from '@shipium-corp/shipium-ui-theme';

const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
    },
    '&& table > .ant-table-tbody > tr > td': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
    // disable hover
    '& .ant-table-tbody > tr.ant-table-row:hover > td': {
      background: 'unset',
    },
  },
  buttonLink: {
    fontFamily: 'monospace',
    fontSize: '1em',
    padding: 0,
  },
  button: {
    fontFamily: 'monospace',
    fontSize: '0.9em',
    width: '100px',
  },
});

export interface SimulationReportingTableProps {
  simulation?: SimulationDetail;
  dimensionTitle: string;
  onDetailClick?: (record: SimulationReportingTableRecord) => void;
  valueType: 'number' | 'percent' | 'currency';
  colorDifference?: boolean;
  invertColors?: boolean;
  showPercentageOfTotal?: boolean;
  showDifference?: boolean;
  includeHistorical?: boolean;
  records: SimulationReportingTableRecord[];
}
export const SimulationReportingTable = ({
  simulation,
  onDetailClick,
  dimensionTitle,
  colorDifference,
  invertColors,
  valueType,
  showDifference,
  showPercentageOfTotal,
  includeHistorical,
  records = [],
}: SimulationReportingTableProps) => {
  const classes = useStyles();
  const loading = useStoreState((state) => state.loading.loadingFlags.simulation);

  const goodColor = colorDifference ? (invertColors ? palette.Coral.normal : palette.Teal.normal) : 'inherit';
  const badColor = colorDifference ? (invertColors ? palette.Teal.normal : palette.Coral.normal) : 'inherit';

  const simulationTotal = records.reduce((acc, row) => acc + row.simulationValue, 0);
  const historicalTotal = records.reduce((acc, row) => acc + (row.historicalValue || 0), 0);

  if (!includeHistorical) {
    showDifference = false;
  }

  const getColor = (val: number) => {
    if (val === 0) return 'inherit';
    return val > 0 ? badColor : goodColor;
  };

  const renderCaret = (val: number) => {
    const color = getColor(val);
    if (val === 0) return null;

    return val > 0 ? <CaretUpOutlined style={{ color }} /> : <CaretDownOutlined style={{ color }} />;
  };

  const standardColumns: ColumnsType<SimulationReportingTableRecord> = [
    {
      title: dimensionTitle,
      key: 'dimension',
      align: 'left',
      sorter: (a, b) => a.dimension.localeCompare(b.dimension),
      render: (val: SimulationReportingTableRecord) => {
        return val.dimension;
      },
    },
    {
      title: 'Historical',
      key: 'historicalValue',
      sorter: (a, b) => (a.historicalValue ?? 0) - (b.historicalValue ?? 0),
      align: 'right',
      render: (val: SimulationReportingTableRecord) => {
        if (val.historicalValue && valueType === 'currency') {
          return <span>${val.historicalValue.toLocaleString()}</span>;
        }
        return val.historicalValue;
      },
    },
    {
      title: 'Simulation',
      key: 'simulationValue',
      sorter: (a, b) => a.simulationValue - b.simulationValue,
      align: 'right',
      render: (val: SimulationReportingTableRecord) => {
        if (valueType === 'currency') {
          return <span>${val.simulationValue.toLocaleString()}</span>;
        }
        return val.simulationValue;
      },
    },
  ];

  const percentageOfTotalColumns: ColumnsType<SimulationReportingTableRecord> = [
    {
      title: 'Historical %',
      key: 'historicalValue',
      align: 'right',
      sorter: (a, b) => (a.historicalValue ?? 0) / historicalTotal - (b.historicalValue ?? 0) / historicalTotal,
      render: (val: SimulationReportingTableRecord) => {
        if (val.historicalValue === undefined) {
          return;
        }
        const percentage = (val.historicalValue / historicalTotal) * 100;
        return <span>{percentage.toFixed(1)}%</span>;
      },
    },
    {
      title: 'Simulation %',
      key: 'simulationPercentValue',
      defaultSortOrder: 'descend',
      align: 'right',
      sorter: (a, b) => a.simulationValue / simulationTotal - b.simulationValue / simulationTotal,
      render: (val: SimulationReportingTableRecord) => {
        const percentage = (val.simulationValue / simulationTotal) * 100;
        return <span>{percentage.toFixed(1)}%</span>;
      },
    },
  ];

  const differenceColumns: ColumnsType<SimulationReportingTableRecord> = [
    {
      title: 'Difference',
      key: 'differenceValue',
      sorter: (a, b) => (a.differenceValue ?? 0) - (b.differenceValue ?? 0),
      align: 'right',
      render: (val: SimulationReportingTableRecord) => {
        if (val.differenceValue === undefined) {
          return;
        }
        const color = getColor(val.differenceValue ?? 0);
        return (
          <span style={{ color }}>
            {val.differenceValue.toLocaleString()}
            {renderCaret(val.differenceValue)}
          </span>
        );
      },
    },
    {
      title: 'Difference %',
      key: 'differencePercentage',
      sorter: (a, b) => (a.differencePercentage ?? 0) - (b.differencePercentage ?? 0),
      align: 'right',
      render: (val: SimulationReportingTableRecord) => {
        if (val.differencePercentage === undefined) {
          return;
        }
        const color = getColor(val.differencePercentage);
        return (
          <span style={{ color }}>
            {val.differencePercentage}%{renderCaret(val.differencePercentage)}
          </span>
        );
      },
    },
  ];

  const columns = standardColumns;

  if (showPercentageOfTotal) {
    columns.push(...percentageOfTotalColumns);
  }

  if (showDifference) {
    columns.push(...differenceColumns);
  }

  if (typeof onDetailClick === 'function') {
    columns.push({
      title: 'Details',
      key: 'details',
      align: 'left',
      render: (val: SimulationReportingTableRecord) => {
        return (
          <Button type="link" onClick={() => onDetailClick(val)}>
            <FileSearchOutlined style={{ fontSize: '16px' }} />
          </Button>
        );
      },
    });
  }

  const finalColumns = includeHistorical
    ? columns
    : columns.filter((col) => !col.key?.toString().includes('historical'));
  return (
    <Table
      loading={loading}
      size={'small'}
      columns={finalColumns}
      dataSource={records}
      pagination={false}
      bordered={false}
      className={classes.tableStyles}
    />
  );
};
